import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Toggle, Button } from 'rsuite';
import { useClient } from '../../react-hooks/useClient';
import { useAureliaI18n } from '../../react-hooks/useAureliaI18n';
import { useFlash } from '../../react-hooks/useFlash';
import { EventAggregator } from 'aurelia-event-aggregator';
import { Container } from 'aurelia-dependency-injection';

const ea = Container.instance.get(EventAggregator);

const mergeItems = (permitted, active) => {
    const merged = permitted.map(defaultItem => {
        const found = active?.find(i => i.key === defaultItem.key);
        return found ? { ...defaultItem, ...found } : { ...defaultItem, visible: false };
    });

    if (active && active.length > 0) {
        merged.sort((a, b) => a.weight - b.weight);
    }

    return merged;
};

const TopRightMenuSettings = ({ topRightItems, allPermittedItems, user, onSaved }) => {
    const client = useClient();
    const { t } = useAureliaI18n();
    const flash = useFlash();
    const [items, setItems] = useState(mergeItems(allPermittedItems, topRightItems));
    const [saving, setSaving] = useState(false);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const dragItem = useRef(null);

    useEffect(() => {
        setItems(mergeItems(allPermittedItems, topRightItems));
    }, [topRightItems]);

    const toggleVisible = useCallback((key) => {
        setItems(prev => prev.map(item =>
            item.key === key ? { ...item, visible: !item.visible } : item
        ));
    }, []);

    const onDragStart = useCallback((index) => {
        dragItem.current = index;
    }, []);

    const onDragOver = useCallback((e, index) => {
        e.preventDefault();
        if (dragOverIndex !== index) setDragOverIndex(index);
    }, [dragOverIndex]);

    const onDrop = useCallback((index) => {
        const dragIndex = dragItem.current;
        if (dragIndex === null || dragIndex === index) {
            setDragOverIndex(null);
            return;
        }
        setItems(prev => {
            const newItems = [...prev];
            const dragged = newItems[dragIndex];
            newItems.splice(dragIndex, 1);
            newItems.splice(index, 0, dragged);
            return newItems.map((item, i) => ({ ...item, weight: (i + 1) * 10 }));
        });
        dragItem.current = null;
        setDragOverIndex(null);
    }, []);

    const onDragEnd = useCallback(() => {
        dragItem.current = null;
        setDragOverIndex(null);
    }, []);

    const save = useCallback(async () => {
        setSaving(true);
        try {
            const payload = items.map(item => JSON.stringify({
                key: item.key,
                visible: item.visible,
                weight: item.weight,
            }));

            await client.put(
                'user/settings-top-right-menu-items/' + user.id,
                { settings: { userSettings: { topRightMenuItems: payload } } }
            );

            ea.publish('sio_form_post_submit', {
                config: { modelId: 'user/user' },
            });

            if (onSaved) onSaved();

        } catch (e) {
            flash.error('sio.error.save_failed');
        } finally {
            setSaving(false);
        }
    }, [items, user, client, onSaved]);

    return (
        <div>
            <p className="tw-mb-4 tw-text-gray-400 tw-text-sm">
                {t('user.settings.topRightMenuItems.help')}
            </p>
            {items.map((item, index) => (
                <div
                    key={item.key}
                    draggable
                    onDragStart={() => onDragStart(index)}
                    onDragOver={(e) => onDragOver(e, index)}
                    onDrop={() => onDrop(index)}
                    onDragEnd={onDragEnd}
                    className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-mb-1 tw-rounded tw-cursor-grab"
                    style={{
                        background: dragOverIndex === index ? '#f0f0f0' : '#fff',
                        border: dragOverIndex === index ? '1px solid #aaa' : '1px solid #e5e5e5',
                    }}
                >
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <i className="fa fa-grip-lines tw-text-gray-300"></i>
                        <span>{t('user.settings.topRightMenuItems.items.' + item.key)}</span>
                    </div>
                    <Toggle
                        checked={item.visible}
                        onChange={() => toggleVisible(item.key)}
                        size="sm"
                    />
                </div>
            ))}
            <div className="tw-mt-4 tw-flex tw-justify-end">
                <Button
                    appearance="primary"
                    onClick={save}
                    loading={saving}
                    size="sm"
                >
                    {t('sio.save')}
                </Button>
            </div>
        </div>
    );
};

export default TopRightMenuSettings;
